import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { errorCode } from '@/api/authApi'
import { useAuth } from '@/providers/AuthProvider'
import { safeNext, ssoErrorMessage } from '@/lib/sso'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

const FAILED = 'SSO sign-in failed.'
// Cognito's ?error= (e.g. an IdP user with no linked smart-cgt account); its raw description is not shown.
const IDP_FAILED =
  "SSO sign-in failed. Try again; if it keeps failing, or you don't have a smart-cgt account yet, contact your administrator."

/** `/auth/callback` — Cognito returns here with `?code&state`; the backend finishes the login. */
export function SsoCallbackPage() {
  const { completeSso } = useAuth()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  // The code is single-use: StrictMode's second effect run must not spend it again.
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true
    const params = new URLSearchParams(window.location.search)
    // Drop the code from the address bar and history before anything else.
    window.history.replaceState(window.history.state, '', window.location.pathname)
    const code = params.get('code')
    const state = params.get('state')
    if (params.get('error')) {
      setError(IDP_FAILED)
      return
    }
    if (!code || !state) {
      setError(FAILED)
      return
    }
    // completeSso replaces the stored session only on success; a failed SSO keeps the one in this browser.
    completeSso(code, state)
      .then((next) => navigate(safeNext(next) ?? '/', { replace: true }))
      .catch((err: unknown) => setError(ssoErrorMessage(errorCode(err)) ?? FAILED))
  }, [completeSso, navigate])

  return (
    <div className='flex min-h-screen items-center justify-center bg-muted/30 px-4'>
      <Card className='w-full max-w-sm'>
        <CardHeader className='items-center text-center'>
          <CardTitle className='text-xl'>Single sign-on</CardTitle>
          <CardDescription>{error ? 'Sign-in did not complete' : 'Signing you in…'}</CardDescription>
        </CardHeader>
        <CardContent className='flex flex-col gap-3'>
          {error ? (
            <>
              <p className='text-sm text-destructive' role='alert'>
                {error}
              </p>
              <Link to='/login' className='text-center text-sm text-muted-foreground hover:underline'>
                Back to sign in
              </Link>
            </>
          ) : (
            <Loader2 className='mx-auto h-6 w-6 animate-spin text-muted-foreground' />
          )}
        </CardContent>
      </Card>
    </div>
  )
}
